import { useCallback, useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { DataQuery, Modality } from "@models/ISchema";
import { modalitiesQuery } from "../graphql/modality.queries";
import getCurricularLabel from "../utils/getCurricularLabel";

interface UseGetModalityOptionsParams {
  scenario: string;
  campusId?: string;
  ignoreAccessFilters?: boolean;
  /**
   * Key to reset the search term and page
   */
  key?: string;
}

const useGetModalityOptions = ({
  scenario,
  key,
  ignoreAccessFilters,
}: UseGetModalityOptionsParams) => {
  const client = useApolloClient();
  const [termToSearch, setTermToSearch] = useState("");
  const [modalityPage, setModalityPage] = useState(1);

  useEffect(() => {
    setTermToSearch("");
    setModalityPage(1);
  }, [key]);

  const requestItems = useCallback(
    async (searchTerm = "", page = 1) => {
      const optionsPerPage = 10;

      try {
        const variables = {
          query: modalitiesQuery,
          variables: {
            scenarioId: scenario,
            filter: {
              ...(ignoreAccessFilters !== undefined && { ignoreAccessFilters }),
              pagination: {
                page,
                size: optionsPerPage,
                searchTerm,
              },
            },
          },
        };

        const { data } = await client.query(variables);
        const dataQuery: DataQuery = data.data;
        const pageInfo = dataQuery?.modalities?.pageInfo;

        const options =
          dataQuery?.modalities?.items?.map((modality: Modality) => ({
            label: getCurricularLabel(modality?.code, modality?.name),
            value: modality.id,
            self: modality,
          })) ?? [];

        return { pageInfo, options };
      } catch (error) {
        console.error(error);
        return {};
      }
    },
    [client, modalityPage, ignoreAccessFilters],
  );

  const loadOptions = async (newSearchTerm: string) => {
    let searchPage = 1;

    if (termToSearch === newSearchTerm) {
      searchPage = modalityPage;
    } else {
      setTermToSearch(newSearchTerm);
    }

    const { pageInfo, options } = await requestItems(newSearchTerm, searchPage);
    setModalityPage(modalityPage + 1);

    return {
      options: options,
      hasMore: pageInfo?.hasNextPage,
      additional: { page: modalityPage },
    };
  };

  return {
    loadOptions,
  };
};

export default useGetModalityOptions;
